import { useCallback } from "react";
import { FileRejection, useDropzone } from "react-dropzone";

import DropZoneIcon from "@mui/icons-material/UploadFileRounded";
import { List, ListItem } from "@mui/material";
import { DROPZONE_ACCEPTED_FILES, DROPZONE_MAX_FILE_SIZE } from "@shared";

import { UploadFormatDialog } from "@/common/components/layout/components/dropzone/components/upload-format-dialog/UploadFormatDialog";
import { useDropZoneUpload } from "@/common/components/layout/components/dropzone/hooks/useDropZoneUpload";
import { useTranslation } from "@/translations";

import { NavigationActionItem } from "./components/item/NavigationActionItem";

interface Props {
  isCollapsed: boolean;
  isDropZoneOpen: boolean;
  onDropZoneToggle: () => void;
  "aria-label"?: string;
}

export function NavigationActions({ isCollapsed, isDropZoneOpen, onDropZoneToggle, "aria-label": ariaLabel }: Props) {
  const { onDrop, formatDialog } = useDropZoneUpload();
  const { t } = useTranslation();

  const handleDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      onDrop(acceptedFiles, rejectedFiles);
      if (acceptedFiles.length > 0 && !isDropZoneOpen) {
        onDropZoneToggle();
      }
    },
    [onDrop, isDropZoneOpen, onDropZoneToggle]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDrop,
    accept: DROPZONE_ACCEPTED_FILES,
    maxSize: DROPZONE_MAX_FILE_SIZE,
    noClick: true,
    noKeyboard: true,
  });

  return (
    <>
      <List disablePadding aria-label={ariaLabel} sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <ListItem disablePadding>
          <input {...getInputProps()} aria-label={t("dropzone.title")} />
          <NavigationActionItem
            navigationItem={{
              label: "layout.navigation.dropzone",
              icon: <DropZoneIcon />,
            }}
            isCollapsed={isCollapsed}
            isSelected={isDropZoneOpen}
            rootProps={getRootProps({
              role: "button",
            })}
            onClick={onDropZoneToggle}
            sx={(theme) => ({
              borderRadius: "4px",
              ...(!isDropZoneOpen && {
                border: `1px dashed ${isDragActive ? theme.palette.primary.main : theme.palette.action.active}`,
                ...(isDragActive && { backgroundColor: theme.palette.action.hover }),
              }),
            })}
          />
        </ListItem>
      </List>

      <UploadFormatDialog state={formatDialog} />
    </>
  );
}
